<template>
  <LMap
    @update:bounds="$emit('update:bounds', $event)"
    @update:center="$emit('update:center', $event)"
    @ready="ready($event)"
    :style="{ width, height }"
    :zoom.sync="zoom"
    :bounds="bounds"
    :center="center"
    :maxZoom="maxZoom"
    :minZoom="minZoom"
    class="leaply map"
    ref="map"
  >
    <LTileLayer
      :url="tileUrl"
      attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors &copy; <a href='https://carto.com/attributions'>CARTO</a>"
    />

    <v-marker-cluster :options="clusterOptions" ref="cluster">
      <template v-for="location in locations">
        <slot name="marker" v-bind="location">
          <LeaplyMapMarker :key="`location-marker-${location.id}`" :marker="location" />
        </slot>
      </template>
    </v-marker-cluster>

    <LControl position="bottomleft">
      <slot name="legend"> </slot>
    </LControl>
  </LMap>
</template>

<script>
// Components
import { LControl, LMap, LTileLayer } from 'vue2-leaflet'
import LeaplyMapMarker from '@/components/maps/LeaplyMapMarker'
import VMarkerCluster from 'vue2-leaflet-markercluster'

// Helpers
import _ from 'lodash'
import { latLngBounds, latLng } from 'leaflet'

const DEFAULT_CENTER = [-33.925902, 18.419082]

// Vue Component
export default {
  name: 'LeaplyMap',

  components: { LControl, LMap, LTileLayer, LeaplyMapMarker, VMarkerCluster },

  created() {
    this.$store.dispatch('geolocation/getCurrentLocation').then(() => {
      this.initializeBounds()
      this.initializeCenter()
    })
  },

  props: {
    defaultCenter: Array,
    height: { String, default: '100%' },
    markers: { type: Array, default: () => [] },
    spreadMarkers: { type: Boolean, default: false },
    useQueryParams: { type: Boolean, default: false },
    width: { type: String, default: '100%' },
    maxZoom: { type: Number, default: 17 },
    minZoom: { type: Number, default: 5 },
  },

  data() {
    return {
      bounds: null,
      center: this.defaultCenter || DEFAULT_CENTER,
      clusterOptions: {
        disableClusteringAtZoom: 12,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
      },
      zoom: 12,
    }
  },

  computed: {
    tileUrl() {
      return `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(process.env.VUE_APP_CARTO_API_KEY)}`
    },

    centerTrigger() {
      return _.get(this, '$route.query.center')
    },

    groupedLocations() {
      const locations = JSON.parse(JSON.stringify(this.markerLocations))
      return _.groupBy(locations, l => `${l.latitude}${l.longitude}`)
    },

    locations() {
      return this.spreadMarkers ? this.spreadOutLocations : this.markerLocations
    },

    markerLocations() {
      const markers = JSON.parse(JSON.stringify(this.markers))
      return markers.filter(m => m.latitude && m.longitude)
    },

    spreadOutLocations() {
      const groups = JSON.parse(JSON.stringify(this.groupedLocations))
      return _.flatten(
        Object.keys(groups).map(group => {
          groups[group].forEach((marker, i) => {
            marker.latitude += (i / 3000) * Math.cos(Math.pow(Math.E, i / 10))
            marker.longitude += (i / 3000) * Math.sin(Math.pow(Math.E, i / 10))
          })
          return groups[group]
        })
      )
    },
  },

  watch: {
    centerTrigger: {
      handler(center, oldCenter) {
        if (this.useQueryParams && center && !_.isEqual(center, oldCenter)) {
          this.initializeCenter()
        }
      },
    },
  },

  methods: {
    initializeBounds() {
      if (!_.get(this, 'markers[1]')) return

      let minLat
      let maxLat
      let minLong
      let maxLong
      for (let marker of this.markers) {
        if (typeof minLat == 'undefined' || minLat > marker.latitude) minLat = marker.latitude
        if (typeof maxLat == 'undefined' || maxLat < marker.latitude) maxLat = marker.latitude
        if (typeof minLong == 'undefined' || minLong > marker.longitude) minLong = marker.longitude
        if (typeof maxLong == 'undefined' || maxLong < marker.longitude) maxLong = marker.longitude
      }
      this.bounds = latLngBounds([[minLat, minLong], [maxLat, maxLong]])
    },
    initializeCenter() {
      if (this.useQueryParams && this.centerTrigger) {
        this.center = this.centerTrigger.split(',')
      } else if (this.defaultCenter) {
        return // use prop value
      } else if (this.locations.length > 0) {
        this.center = [
          this.locations.map(x => x.latitude).reduce((a, b) => (a += b)) / this.locations.length,
          this.locations.map(x => x.longitude).reduce((a, b) => (a += b)) / this.locations.length,
        ]
      }
    },
    ready(mapObject) {
      this.$emit('update:bounds', mapObject.getBounds())
      this.$emit('update:center', mapObject.getCenter())
      mapObject.scrollWheelZoom.disable()
    },
  },
}
</script>

<style lang="scss">
@import '~leaflet.markercluster/dist/MarkerCluster.css';
@import '~leaflet.markercluster/dist/MarkerCluster.Default.css';

.leaply.map {
  z-index: 0;
  background: #fbf8f3;
}
</style>
